import React, { ReactNode, useState } from 'react'

import { SxProps } from '@mui/material'
import { Color } from 'themes'

import Box from 'components_new/atoms/Box'
import Icon, { IconProps } from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Text from 'components_new/atoms/Text'

import { dict } from './lib'

interface StatusBoxProps {
  actions?: ReactNode
  allowClose?: boolean
  children?: ReactNode
  color?: Color | 'brand' | 'error' | 'info' | 'success' | 'warning'
  disableBorder?: boolean
  icon?: Pick<IconProps, 'name'>
  severity: 'info' | 'success' | 'warning' | 'error'
  size?: 'small' | 'medium'
  sx?: SxProps
  title?: string | ReactNode
}

const StatusBox = (props: StatusBoxProps) => {
  const {
    actions,
    allowClose = true,
    children,
    color,
    disableBorder,
    icon,
    severity,
    size,
    sx,
    title
  } = props

  const [open, setOpen] = useState<boolean>(true)

  // if color not given then fallback on severity
  const mainColor = color ?? severity

  const textVariant = size === 'small' ? 'body2' : 'body1'

  return (
    <Box
      sx={{
        alignItems: !children ? 'center' : undefined,
        bgcolor: `${mainColor}.transparent`,
        borderRadius: 1,
        border: disableBorder ? undefined : '1px solid',
        borderColor: `${mainColor}.background`,
        display: open ? 'flex' : 'none',
        gap: 1,
        p: size === 'small' ? 1 : 2,
        ...sx
      }}
    >
      <Icon
        fontSize={size}
        name={icon?.name ?? dict[severity].iconName}
        sx={{ color: `${mainColor}.text` }}
      />
      <Box sx={{ flex: 1, height: 'fit-content' }}>
        {title ? (
          <Text
            component="div"
            fontWeight="bold"
            sx={{ maxWidth: 'title' }}
            variant={textVariant}
          >
            {title}
          </Text>
        ) : null}
        {children ? (
          <Text
            color="text.secondary"
            component="div"
            sx={{ maxWidth: 'content', mt: '0.25em' }}
            variant={textVariant}
          >
            {children}
          </Text>
        ) : null}
      </Box>
      {actions ? (
        <Box sx={{ display: 'flex', gap: 1, height: 'fit-content' }}>
          {actions}
        </Box>
      ) : null}
      {allowClose ? (
        <Box>
          <IconButton
            onClick={() => setOpen(false)}
            size="small"
            sx={{ mt: children ? -0.75 : 0 }}
          >
            <Icon color={mainColor} fontSize="small" name="Close" />
          </IconButton>
        </Box>
      ) : null}
    </Box>
  )
}

export default StatusBox
